// tiered_offering — pricing tiers with conditional discounts; selection saved to capture_selection_to + the appraisal.
import React, { useState } from 'react';
import { Layers } from 'lucide-react';
import { StepCard, StepNav, ErrorLine, humanise, writeback } from './_stepKit';

// "annual_check:base=200,group_discount=10%,prebook_discount=15%" → [{key, base, discounts:[{key,pct}]}]; tiers split on ";" or newline.
export function parseTiers(text) {
  return String(text || '').split(/[;\n]/).map((t) => t.trim()).filter(Boolean).map((t) => {
    const [key, rest = ''] = t.split(':');
    const tier = { key: key.trim(), base: 0, discounts: [] };
    rest.split(',').map((s) => s.trim()).filter(Boolean).forEach((kv) => {
      const [k, v = ''] = kv.split('=').map((s) => s.trim());
      if (k === 'base') tier.base = Number(v) || 0;
      else tier.discounts.push({ key: k, pct: parseFloat(v) || 0 });
    });
    return tier;
  });
}

const money = (v) => `$${Number(v || 0).toLocaleString('en-NZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function TieredOffering({ action, value, onChange, onNext, onBack, canBack, capturedData }) {
  const p = action.params || {};
  const tiers = parseTiers(p.options);
  const multi = p.allow_multi === true || String(p.allow_multi).toLowerCase() === 'true';
  const [sel, setSel] = useState(value?.selected || {});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const priceOf = (t, picked) => t.base * (1 - picked.reduce((a, k) => a + (t.discounts.find((d) => d.key === k)?.pct || 0), 0) / 100);
  const toggleTier = (k) => setSel((s) => (s[k] ? Object.fromEntries(Object.entries(s).filter(([x]) => x !== k)) : { ...(multi ? s : {}), [k]: [] }));
  const toggleDisc = (k, d) => setSel((s) => ({ ...s, [k]: s[k].includes(d) ? s[k].filter((x) => x !== d) : [...s[k], d] }));
  const out = { selected: sel, lines: tiers.filter((t) => sel[t.key]).map((t) => ({ tier: t.key, discounts: sel[t.key], price: Math.round(priceOf(t, sel[t.key]) * 100) / 100 })) };

  const next = async () => {
    setBusy(true); setErr('');
    try { await writeback(capturedData, 'maintenance_selection', out, (p.capture_selection_to || 'selection').replace(/\./g, '_')); onChange(out); onNext(out); }
    catch (e) { setErr(e?.response?.data?.detail || e.message || 'Save failed'); } finally { setBusy(false); }
  };

  return (
    <div>
      <StepCard icon={Layers} title="Offer options" testid="wf-tiered-offering">
        {tiers.map((t) => (
          <div key={t.key} className={`p-3 rounded border transition-colors ${sel[t.key] ? 'border-sky-400 bg-sky-50' : 'border-slate-200'}`}>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type={multi ? 'checkbox' : 'radio'} checked={!!sel[t.key]} onChange={() => toggleTier(t.key)} data-testid={`wf-tier-${t.key}`} />
              <span className="font-semibold text-slate-800">{humanise(t.key)}</span>
              <span className="ml-auto font-mono text-sm" data-testid={`wf-tier-price-${t.key}`}>{money(sel[t.key] ? priceOf(t, sel[t.key]) : t.base)}</span>
            </label>
            {sel[t.key] && t.discounts.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-3 pl-6">
                {t.discounts.map((d) => (
                  <label key={d.key} className="flex items-center gap-1.5 text-xs text-slate-700 cursor-pointer">
                    <input type="checkbox" checked={sel[t.key].includes(d.key)} onChange={() => toggleDisc(t.key, d.key)} data-testid={`wf-tier-${t.key}-disc-${d.key}`} />
                    {humanise(d.key)} (−{d.pct}%)
                  </label>
                ))}
              </div>
            )}
          </div>
        ))}
        {tiers.length === 0 && <div className="text-sm text-slate-500">No options configured for this step.</div>}
        <ErrorLine err={err} testid="wf-tiered-offering-err" />
      </StepCard>
      <StepNav canBack={canBack} onBack={onBack} onNext={next} busy={busy} label={out.lines.length ? 'Next →' : 'Skip (none chosen) →'} />
    </div>
  );
}
